import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { useState, useEffect } from "react";
import {
  BCPArrowRightFromBracket,
  BCPCircleCheck
} from "../../components/icons/bcp/BCPIcons";
import { MyWarhammerLinkButton } from "../../components/alerts/DefaultAlerts";
import { GWWarhammerIcon } from "../../components/icons/gw/GWIcons";
import { oidButtonSX } from "../user/Account/components/IdentitiesTab";
import * as Yup from "yup";
import { Formik } from "formik";
import { postNotification } from "../../components/utilities/Notifications";
import { delay, postData } from "../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { decodeToken } from "react-jwt";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { refreshTokens } from "../../components/user/BCPAuth";
import { useNavigate } from "react-router";

const AUTH_CLIENT_ID = getEnvironmentVariable("AUTH_CLIENT_ID");

export default function SubscriptionTrial(props) {
  const theme = useTheme();
  const navigate = useNavigate();

  const [identity, setIdentity] = useState(null);
  const [processing, setProcessing] = useState(false);
  const buttonSX = oidButtonSX("GamesWorkshop");

  const pollIdentity = async (identityToken) => {
    try {
      if (!identityToken.myWarhammerIdentity) {
        identityToken.myWarhammerIdentity = JSON.parse(
          identityToken?.federatedIdentities
        ).filter((el) => el.providerName === "GamesWorkshop")?.[0];
        setIdentity(identityToken);
        await delay(5000);
        await refreshTokens();
      }
    } catch {}
  };

  const pollUser = async () => {
    const idTokenString = localStorage?.getItem(
      `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.idToken`
    );
    const identityToken = idTokenString
      ? decodeToken(idTokenString)
      : undefined;

    if (identityToken) {
      setIdentity(identityToken);

      try {
        identityToken.myWarhammerIdentity = identityToken?.federatedIdentities
          ?.length
          ? JSON.parse(identityToken?.federatedIdentities).filter(
              (el) => el.providerName === "GamesWorkshop"
            )?.[0]
          : null;
        pollIdentity(identityToken);
      } catch {}

      return;
    }

    await delay(5000);
    pollUser();
  };

  useEffect(() => {
    pollUser();
    // eslint-disable-next-line
  }, []);

  return (
    <Box width="100%">
      {processing && <ModalLoadingFull />}
      <Container
        maxWidth="lg"
        sx={{
          pt: 2,
          pb: 2
        }}
      >
        <Formik
          initialValues={{
            couponCode: ""
          }}
          enableReinitialize
          validationSchema={Yup.object().shape({
            couponCode: Yup.string().required("Required")
          })}
          onSubmit={async (values) => {
            if (identity?.subscriber === "true") {
              postNotification(
                "info",
                "Free Trial not available for existing subscribers."
              );
              return;
            }

            try {
              setProcessing(true);
              var body = {
                campaign: "MYWARHAMMERLINK",
                couponCode: values.couponCode
              };
              let subscription = await postData(
                "/subscriptions/trialsubscription",
                {
                  body: body
                }
              );

              navigate(`/subscription/${subscription.id}`);
            } catch (err) {
              postNotification("error", err);
            } finally {
              setProcessing(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container gap={2} alignItems="center" sx={{ p: 2 }}>
                <Grid item xs={12} textAlign="center">
                  <img
                    src={`/images/campaigns/mywarhammerlink/Warhammer${
                      theme?.palette?.mode === "dark" ? "Dark" : ""
                    }.png`}
                    alt="US Open Tacoma"
                    style={{
                      maxWidth: "100%",
                      height: "250px",
                      maxHeight: "100%"
                    }}
                  />
                </Grid>
                <Grid item xs={12} textAlign="center">
                  <Typography variant="h3" gutterBottom>
                    Free 6 Month Trial
                  </Typography>
                  <Typography variant="caption">
                    {`(With My Warhammer Account Linked. New subscribers only.)`}
                  </Typography>
                </Grid>
                <Grid item xs={12} justifyItems="center" textAlign="center">
                  <Grid
                    container
                    gap={2}
                    alignItems="center"
                    sx={{ p: 2, maxWidth: "400px" }}
                  >
                    <Grid item xs={12} textAlign="center">
                      {!identity ? (
                        <Button
                          fullWidth
                          href="/login"
                          variant="contained"
                          color="secondary"
                          startIcon={<BCPArrowRightFromBracket />}
                        >
                          Login / Create Account
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          variant="contained"
                          color="success"
                          startIcon={<BCPArrowRightFromBracket />}
                          endIcon={<BCPCircleCheck />}
                        >
                          Logged In
                        </Button>
                      )}
                    </Grid>
                    <Grid item xs={12} textAlign="center">
                      {identity?.myWarhammerIdentity ? (
                        <Button
                          fullWidth
                          variant="contained"
                          color="warning"
                          sx={{ textTransform: "unset", mt: 2, ...buttonSX }}
                          startIcon={<GWWarhammerIcon />}
                          endIcon={<BCPCircleCheck />}
                        >
                          My Warhammer Linked
                        </Button>
                      ) : (
                        <MyWarhammerLinkButton
                          fullWidth
                          disabled={!identity}
                          target="_blank"
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} textAlign="center">
                      <TextField
                        name="couponCode"
                        label="Coupon"
                        value={values.couponCode}
                        disabled={!identity?.myWarhammerIdentity}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        required
                        onBlur={(e) => {
                          e.target.value = e.target.value.toUpperCase();
                          handleBlur(e);
                        }}
                        onChange={(e) => {
                          e.target.value = e.target.value.toUpperCase();
                          handleChange(e);
                        }}
                        error={Boolean(touched.couponCode && errors.couponCode)}
                        helperText={touched.couponCode && errors.couponCode}
                      />
                    </Grid>
                    <Grid item xs={12} textAlign="center">
                      <Button
                        fullWidth
                        color="success"
                        variant="contained"
                        type="submit"
                        disabled={!values.couponCode}
                      >
                        Subscribe
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Container>
    </Box>
  );
}
